"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SpatialCommandCenter from "@/components/spatial/SpatialCommandCenter";
import MindalooStarfield from "@/components/mindaloo/MindalooStarfield";
import BrandStyles from "@/components/branding/BrandStyles";
import KarmaDynamicIsland from "./KarmaDynamicIsland";
import KarmaNeuralShelf from "./KarmaNeuralShelf";
import KarmaCommandBar, { type ActiveTileInfo } from "./KarmaCommandBar";
import VoiceGlowRing from "@/components/mindaloo/VoiceGlowRing";
import MindalooSlideOver, { type SlidePanel } from "./MindalooSlideOver";
import MindalooThoughtStream from "./MindalooThoughtStream";
import MindalooMemoryPanel from "./MindalooMemoryPanel";
import MindalooMirrorPanel from "./MindalooMirrorPanel";
import MindalooAutonomousPanel from "./MindalooAutonomousPanel";
import MindalooAppearancePanel from "./MindalooAppearancePanel";
import CouncilPanel from "@/components/spatial/CouncilPanel";
import type { RaisedHand } from "@/lib/mindaloo/proactiveTriggers";
import MindalooChatTile from "./MindalooChatTile";
import CommandPalette from "@/components/ui/CommandPalette";
import ToastContainer from "@/components/ui/Toast";
import { useAwarenessState, AWARENESS_STATES } from "@/lib/karma-ac/useAwarenessState";
import { useMindalooSession } from "@/lib/mindaloo/useMindalooSession";
import { useMindalooProfile } from "@/lib/mindaloo/useMindalooProfile";
import { useMindalooAtlas } from "@/lib/mindaloo/useMindalooAtlas";
import { useKarmaV11Stream } from "@/lib/mindaloo/useKarmaV11Stream";
import { VoicePilotSession, estimateTtsDuration } from "@/lib/mindaloo/voicePilot";
import type { AiProfile } from "@/types/ai";

const DEFAULT_KARMA_PROFILE: AiProfile = {
  id: "karma-default",
  name: "Karma",
  modePreset: "business",
  brand: {
    primaryColor: "#6366f1",
    secondaryColor: "#a855f7",
    iconDataUrl: null,
  },
  tiles: [],
  sliders: {
    formality: 50,
    verbosity: 40,
    boldness: 50,
    proactivity: 60,
    warmth: 60,
    creativity: 50,
    empathy: 60,
  },
  privacyMode: "smart",
  agentModeEnabled: true,
  plan: "pro",
  isPublic: false,
  version: 1,
  viewCount: 0,
  cloneCount: 0,
  ownerId: "default",
  createdAt: Date.now(),
  updatedAt: Date.now(),
};
import { executeVoicePilotActions } from "@/lib/mindaloo/voicePilotActions";
import { runPlannedTask } from "@/lib/mindaloo/taskPlanner";
import { eventBus } from "@/lib/utils/eventBus";
import { neuralLinkStore } from "@/lib/chat/neuralLinkStore";
import { archiveClosedGroup } from "@/lib/chat/multiChatHistory";
import type { MultiChatSession } from "@/lib/db/schema";
import { getUserId } from "@/lib/user/identity";
import {
  getCouncilRound1,
  continueCouncilDeliberation,
  clearConferring,
  type CouncilTile,
  type CouncilRoundResult,
} from "@/lib/mindaloo/councilDeliberation";
import type { ResponseMode } from "@/lib/ai/responseModes";

import { db } from "@/lib/db";
import { MindalooErrorBoundary } from "./MindalooErrorBoundary";
import MindalooOnboarding, { useOnboarding } from "./MindalooOnboarding";

// Matches ChatInterface.tsx's own copy — framing text appended after this
// delimiter reaches the model (see sendMessage there) but never gets
// rendered as part of the chat bubble or forwarded into the Atlas Core
// transcript below. Duplicated rather than shared across files since it's
// a 2-line pure function and not worth a new module for.
const HIDDEN_INSTRUCTION_DELIMITER = "\n\n<!--karma-hidden-instruction-->\n";
function stripHiddenInstruction(text: string): string {
  const idx = text.indexOf(HIDDEN_INSTRUCTION_DELIMITER);
  return idx === -1 ? text : text.slice(0, idx);
}

// Linked tiles broadcast to everyone by default, but a message that clearly
// names specific ones ("chat A and chat C", "tell tile B", by the tile's
// actual name, or an ordinal like "the second one") should only go to
// those — not force the user to unlink everyone else first just to talk to
// one. Position labels (A/B/C.../1/2/3...) are assigned by array order
// since tiles don't have their own persistent labels yet.
function resolveBroadcastTargets(text: string, linkedIds: string[], tiles: ActiveTileInfo[]): string[] {
  const linkedTiles = linkedIds
    .map((id) => tiles.find((t) => t.id === id))
    .filter((t): t is ActiveTileInfo => !!t);
  if (linkedTiles.length === 0) return linkedIds;

  const lower = text.toLowerCase();
  const ORDINAL_WORDS = ["first", "second", "third", "fourth", "fifth", "sixth"];
  const matched = new Set<string>();

  linkedTiles.forEach((tile, i) => {
    const letter = String.fromCharCode(65 + i).toLowerCase(); // a, b, c...
    const number = String(i + 1);
    const nameMatch = tile.name && new RegExp(`\\b${tile.name.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(lower);
    const letterMatch = new RegExp(`\\b(chat|tile|agent)\\s+${letter}\\b`).test(lower);
    const numberMatch = new RegExp(`\\b(chat|tile|agent)\\s+${number}\\b`).test(lower);
    const ordinalMatch = ORDINAL_WORDS[i] && new RegExp(`\\bthe\\s+${ORDINAL_WORDS[i]}\\b`).test(lower);
    if (nameMatch || letterMatch || numberMatch || ordinalMatch) matched.add(tile.id);
  });

  return matched.size > 0 ? Array.from(matched) : linkedIds;
}

export default function MindalooShell() {
  const { awareness, accent, setAwarenessState } = useAwarenessState();
  const { isPremiumGodMode, systemTelemetry } = useMindalooSession();
  const { profile, profiles, loading } = useMindalooProfile();
  const { shouldOnboard, completeOnboarding } = useOnboarding();

  const [theme, setTheme] = useState<"night" | "day">("night");
  const [showShelf, setShowShelf] = useState(false);
  const [showThoughtStream, setShowThoughtStream] = useState(false);
  const [showMemory, setShowMemory] = useState(false);
  const [showMirror, setShowMirror] = useState(false);
  const [showAutonomous, setShowAutonomous] = useState(false);
  const [showAppearance, setShowAppearance] = useState(false);
  const [showCouncil, setShowCouncil] = useState(false);
  const [raisedHands, setRaisedHands] = useState<RaisedHand[]>([]);
  const [slidePanel, setSlidePanel] = useState<SlidePanel>(null);
  const [tileCount, setTileCount] = useState(0);
  const [activeTiles, setActiveTiles] = useState<ActiveTileInfo[]>([]);
  const activeTilesRef = useRef<ActiveTileInfo[]>([]);
  useEffect(() => {
    activeTilesRef.current = activeTiles;
  }, [activeTiles]);
  const [status, setStatus] = useState("Focused");
  const [activeProfile, setActiveProfile] = useState<AiProfile | null>(null);
  const [input, setInput] = useState("");
  const [cmdLoading, setCmdLoading] = useState(false);
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceThinking, setVoiceThinking] = useState(false);
  const [voiceContinuous, setVoiceContinuous] = useState(false);
  const voiceContinuousRef = useRef(false);
  // React state isn't synchronously readable within the same tick, so
  // voiceThinking alone can't stop a second transcript that arrives in the
  // same instant from launching a second, overlapping handleVoicePilot
  // call - confirmed live: two turns in flight at once, both playing TTS
  // audio simultaneously ("duplicate voices"). This ref is the actual guard.
  const voiceTurnInFlightRef = useRef(false);
  // voiceTurnInFlightRef only ever gets checked at the *start* of a new
  // turn — it says nothing about whether an *older* turn already in flight
  // is still safe to act on once its fetch/TTS eventually resolves. The
  // 25s force-clear timeout below unblocks future turns from the
  // reentrancy lock, but a slow turn's own promise chain keeps running
  // regardless and, once it finally resolves, still executes actions and
  // plays TTS — landing well after the user moved on. Confirmed live:
  // "ending it and asking again" could make an old, abandoned turn
  // suddenly send its result to a tile seconds later, looking like a
  // random duplicate message. Every turn now captures the counter's
  // current value at start and checks it's still current before doing
  // anything user-visible; starting a new turn bumps it, which silently
  // invalidates whatever came before.
  const voiceTurnGenerationRef = useRef(0);

  // A failed Google sign-in used to redirect back here with zero
  // indication anything went wrong (see app/api/auth/callback/route.ts) —
  // the user just looked signed-out and had to guess why. The callback
  // route now flags it via a query param on failure; this surfaces it as
  // a real toast once, then strips the param so refreshing doesn't repeat it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("auth_error") !== "1") return;
    eventBus.emit("TOAST:SHOW", {
      message: "Sign-in didn't go through — please try again.",
      type: "error",
    });
    params.delete("auth_error");
    const query = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (query ? `?${query}` : ""));
  }, []);

  useEffect(() => {
    const unsub = eventBus.on("TILE:RAISE_HAND", (hand: RaisedHand) => {
      setRaisedHands((prev) => {
        if (prev.some((h) => h.triggerId === hand.triggerId)) return prev;
        return [...prev, hand];
      });
    });
    return () => unsub();
  }, []);

  // Linked tiles: nav bar talks to all linked tiles, and they share perspectives with each other
  const [linkedTileIds, setLinkedTileIds] = useState<string[]>([]);
  const linkedTileIdsRef = useRef<string[]>([]);
  useEffect(() => {
    linkedTileIdsRef.current = linkedTileIds;
    // "...and they share perspectives with each other" was the stated
    // intent since this comment was first written, but nothing ever
    // actually built it — Multichat sent the same question to every linked
    // tile independently, with zero awareness of what the others answered.
    // A full cross-tile-awareness mechanism (neuralLinkStore, injected as
    // "## Neural Link — connected AI peers" in the system prompt server-
    // side) already existed and was fully wired end to end — it just never
    // had anything feeding its connectedChatIds prop. Each tile picks this
    // up itself (see MindalooChatTile.tsx) and computes its own peer list.
    eventBus.emit("SPATIAL:LINKED_TILES_CHANGED", { linkedTileIds });
  }, [linkedTileIds]);

  // Primary tile: the user's designated "lead thread" within a linked
  // group — switchable, so a direction that stalls can hand the lead to
  // whichever tile is actually getting it right without breaking the link.
  // Purely advisory (see neuralLinkStore.buildContext): peers are told to
  // weigh it, not defer to it blindly.
  const [primaryTileId, setPrimaryTileIdState] = useState<string | null>(null);
  const primaryTileIdRef = useRef<string | null>(null);
  useEffect(() => {
    primaryTileIdRef.current = primaryTileId;
    eventBus.emit("SPATIAL:PRIMARY_TILE_CHANGED", { primaryTileId });
  }, [primaryTileId]);
  useEffect(() => {
    // A primary designation only means anything while that tile is still
    // linked into a group of 2+ — dropped the moment either condition
    // stops being true, same as linkedTileIds' own cleanup below.
    if (primaryTileId && (linkedTileIds.length < 2 || !linkedTileIds.includes(primaryTileId))) {
      setPrimaryTileIdState(null);
    }
  }, [primaryTileId, linkedTileIds]);
  const setPrimaryTile = useCallback((tileId: string) => {
    setPrimaryTileIdState((prev) => (prev === tileId ? null : tileId));
  }, []);

  // Late-join synthesis: a tile that links into a group with a conversation
  // already underway used to just sit there — connectedChatIds made it
  // *see* future peer messages, but it had no idea what had already
  // happened, so the first thing the user heard from it was a reply to
  // whatever came next with zero acknowledgment of the run-up. That's what
  // "the other tiles linked at the start didn't do anything" actually
  // was: the newly-joined tile wasn't broken, it just had nothing to say
  // about a conversation it had no visibility into yet. Reuses the same
  // "read what the room said, give your own take" prompt shape Council's
  // round 2 already uses (see continueCouncilDeliberation) — that pattern
  // is proven, just applied on link instead of on a fixed round.
  const triggerLateJoinSynthesis = useCallback(async (newTileId: string, groupIds: string[]) => {
    const peers = neuralLinkStore.readPeers(newTileId, groupIds);
    if (peers.length === 0) return; // nothing prior to catch up on
    const newTile = activeTilesRef.current.find((t) => t.id === newTileId);
    if (!newTile?.profile) return;

    const primary = primaryTileIdRef.current;
    const transcript = peers
      .map((m) => `${m.aiName}${primary && m.sourceId === primary ? " (primary direction)" : ""} (${m.role === "assistant" ? "said" : "was asked"}): ${m.text}`)
      .join("\n\n");
    eventBus.emit("SPATIAL:CONFERRING", { tileId: newTileId, label: "Catching up on the conversation…" });
    try {
      const primaryNote = primary && peers.some((m) => m.sourceId === primary)
        ? " One of them is marked as this group's primary direction — give real weight to it, but still say plainly if you'd go differently."
        : "";
      const prompt = `You've just been linked into an ongoing conversation between other AI tiles. Here's what's been discussed so far:\n\n${transcript}\n\nGive your own take now that you've read it — agree and build on it, add what's missing, or say plainly where you'd go differently.${primaryNote} Speak in your own voice, like you're actually joining the room, not summarizing it.`;
      const res = await fetch("/api/karma-ac/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile: newTile.profile, message: prompt, userId: getUserId(), stream: false }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
      const text = typeof data?.message === "string" ? data.message : "";
      if (!text) return;
      eventBus.emit("SPATIAL:INJECT_ASSISTANT_MESSAGE", { tileId: newTileId, content: text });
      // So this tile's own catch-up reply is itself visible to whichever
      // tile links in after it, same as any other exchange.
      neuralLinkStore.write(newTileId, {
        role: "assistant",
        text,
        aiName: newTile.profile.name,
        timestamp: Date.now(),
      });
    } catch (err) {
      console.error("[late-join synthesis] failed:", err);
    } finally {
      eventBus.emit("SPATIAL:CONFERRING", { tileId: newTileId, label: null });
    }
  }, []);

  const toggleTileLink = useCallback((tileId: string) => {
    // The functional updater form is what makes rapid consecutive toggles
    // (three tiles linked back-to-back) each see the previous call's result
    // instead of racing on a stale closure — that part has to stay. What
    // doesn't belong inside it is the eventBus.emit calls below: React can
    // invoke a state updater during another component's render, and an
    // emit in there synchronously drives ToastContainer's own setState,
    // which is exactly the "Cannot update a component while rendering a
    // different component" violation this used to throw. Stashing the
    // computed result in a plain (call-scoped, not closure-shared) ref and
    // firing the side effects after setLinkedTileIds returns keeps the
    // batching behavior while moving the emits out of the render path.
    const resultRef = { current: linkedTileIdsRef.current };
    setLinkedTileIds((prev) => {
      const next = prev.includes(tileId) ? prev.filter((id) => id !== tileId) : [...prev, tileId];
      resultRef.current = next;
      return next;
    });
    const next = resultRef.current;
    // The link icon's visual change (a faint glow) is easy to miss, and the
    // feature does nothing until 2+ tiles are linked — say so explicitly
    // instead of leaving the user guessing whether the tap registered.
    const tileName = activeTiles.find((t) => t.id === tileId)?.name ?? "Tile";
    if (next.length >= 2) {
      eventBus.emit("TOAST:SHOW", {
        message: `🔗 Linked ${next.length} tiles — the main bar now talks to all of them at once, each answering independently.`,
        type: "success",
      });
      // Linked tiles should actually sit next to each other, not wherever
      // they happened to be dropped — only when a tile was just added to
      // the link (not removed), so unlinking doesn't also re-arrange.
      if (next.includes(tileId)) {
        eventBus.emit("SPATIAL:ARRANGE_TILES", { mode: "grid" });
        triggerLateJoinSynthesis(tileId, next);
      }
    } else if (next.includes(tileId)) {
      eventBus.emit("TOAST:SHOW", {
        message: `Linked ${tileName} — link one more tile to connect them.`,
        type: "info",
      });
    } else {
      eventBus.emit("TOAST:SHOW", { message: `Unlinked ${tileName}.`, type: "info" });
    }
  }, [activeTiles]);
  // Last-known name/profile for every tile this session has ever seen,
  // keyed by id — accumulates rather than replaces, so a tile that just
  // closed (and is therefore already gone from `activeTiles`) can still be
  // identified below when deciding whether its linked group just closed
  // entirely and needs archiving to the History Box.
  const tileInfoCacheRef = useRef<Map<string, ActiveTileInfo>>(new Map());
  useEffect(() => {
    for (const t of activeTiles) tileInfoCacheRef.current.set(t.id, t);
  }, [activeTiles]);
  useEffect(() => {
    // Drop links for tiles that no longer exist. If EVERY tile in the
    // linked group just closed (the "start again" case), that's a group
    // being wrapped up on purpose, not one tile among many disappearing —
    // archive it to the History Box before the ids are gone for good.
    setLinkedTileIds((prev) => {
      const stillOpen = prev.filter((id) => activeTiles.some((t) => t.id === id));
      if (prev.length >= 2 && stillOpen.length === 0) {
        const closedTiles = prev
          .map((id) => tileInfoCacheRef.current.get(id))
          .filter((t): t is ActiveTileInfo => !!t);
        archiveClosedGroup(closedTiles, primaryTileIdRef.current).catch((err) =>
          console.error("[history box] archive failed:", err)
        );
      }
      return stillOpen;
    });
  }, [activeTiles]);

  // Rolling window of recent tile activity so the voice pilot — and, below,
  // Multichat's cross-tile awareness — can "see" the workspace. tileId is
  // tracked alongside tileName so a linked-group broadcast can filter to
  // "every OTHER tile's latest exchange" precisely, without relying on
  // names staying unique (they do now, but this doesn't need to assume it).
  const recentExchangesRef = useRef<Array<{ tileId: string; tileName: string; userText: string; assistantText: string }>>([]);

  // Whichever tile the consciousness bar is currently showing controls for
  // (see SpatialCommandCenter.tsx's SPATIAL:ACTIVE_TILE_CHANGED broadcast) —
  // a multi-step plan triggered while a tile is active scopes to that tile
  // instead of running as an untargeted background task.
  const activeTileRef = useRef<{ id: string; name: string; maximized: boolean } | null>(null);
  useEffect(() => {
    const unsub = eventBus.on(
      "SPATIAL:ACTIVE_TILE_CHANGED",
      ({ tile }: { tile: { id: string; name: string; maximized: boolean } | null }) => {
        activeTileRef.current = tile ? { id: tile.id, name: tile.name, maximized: tile.maximized } : null;
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = eventBus.on(
      "TILE:EXCHANGE",
      ({ tileId, tileName, userText, assistantText }: { tileId: string; tileName: string; userText: string; assistantText: string }) => {
        recentExchangesRef.current.push({ tileId, tileName, userText, assistantText });
        if (recentExchangesRef.current.length > 8) {
          recentExchangesRef.current = recentExchangesRef.current.slice(-8);
        }
        // The Atlas strip's Core tab shows this as "the conversation with
        // Karma" — voiceSession used to only grow from direct voice turns
        // (handleVoicePilot), so typing in the command bar (which just
        // broadcasts to tiles and never sees the reply itself) never showed
        // up there, even though it's just as much a real exchange.
        if (userText.trim() && assistantText.trim()) {
          setVoiceSession((prev) => [
            ...prev,
            { role: "user", content: stripHiddenInstruction(userText) },
            { role: "assistant", content: assistantText },
          ]);
        }
      }
    );
    return () => unsub();
  }, []);
  useEffect(() => {
    voiceContinuousRef.current = voiceContinuous;
  }, [voiceContinuous]);
  const [voiceSession, setVoiceSession] = useState<Array<{ role: "user" | "assistant"; content: string }>>([]);
  const voicePilotRef = useRef<VoicePilotSession | null>(null);
  const voiceInputRef = useRef<{ start: () => void; stop: () => void } | null>(null);
  const lastVoiceTsRef = useRef<number>(0);
  const lastVoiceTextRef = useRef<string>("");
  if (!voicePilotRef.current) {
    voicePilotRef.current = new VoicePilotSession(activeProfile?.name ?? "Karma");
  }
  const [responseMode, setResponseMode] = useState<ResponseMode>("auto");

  // Keep only one overlay/slide-over open at a time so panels don't stack
  const latestProfilesRef = useRef(profiles);
  const latestActiveProfileRef = useRef(activeProfile);
  const latestOpenProfileRef = useRef<(p: AiProfile) => void>(() => {});
  useEffect(() => {
    latestProfilesRef.current = profiles;
    latestActiveProfileRef.current = activeProfile;
  }, [profiles, activeProfile]);

  const prevPanels = useRef({
    showShelf,
    showThoughtStream,
    showMemory,
    showMirror,
    showAutonomous,
    showAppearance,
    showCouncil,
    slidePanel,
  });
  useEffect(() => {
    const current = {
      showShelf,
      showThoughtStream,
      showMemory,
      showMirror,
      showAutonomous,
      showAppearance,
      showCouncil,
      slidePanel,
    };
    const opened = (
      ["showShelf", "showThoughtStream", "showMemory", "showMirror", "showAutonomous", "showAppearance", "showCouncil", "slidePanel"] as const
    ).find((key) => {
      const now = current[key as keyof typeof current] as any;
      const before = prevPanels.current[key as keyof typeof prevPanels.current] as any;
      return now && !before;
    });
    if (opened) {
      if (opened !== "showShelf") setShowShelf(false);
      if (opened !== "showThoughtStream") setShowThoughtStream(false);
      if (opened !== "showMemory") setShowMemory(false);
      if (opened !== "showMirror") setShowMirror(false);
      if (opened !== "showAutonomous") setShowAutonomous(false);
      if (opened !== "showAppearance") setShowAppearance(false);
      if (opened !== "showCouncil") setShowCouncil(false);
      if (opened !== "slidePanel") setSlidePanel(null);
    }
    prevPanels.current = current;
  }, [showShelf, showThoughtStream, showMemory, showMirror, showAutonomous, showAppearance, showCouncil, slidePanel]);

  const userId = typeof window !== "undefined" ? getUserId() : "mindaloo";
  const {
    atlasProcess,
    setAtlasProcess,
    atlasNavigationResult,
    atlasHistory,
    navigateForMessage,
    recordOutcome,
  } = useMindalooAtlas("mindaloo-main");

  const { lines: v11Lines, connected: v11Live } = useKarmaV11Stream(true, userId);
  const v11Preview = v11Lines.filter((l) => l.type === "thought").slice(-1)[0]?.text;

  useEffect(() => {
    if (profile) {
      setActiveProfile(profile);
      voicePilotRef.current?.setAssistantName(profile.name);
    }
  }, [profile]);

  useEffect(() => {
    if (v11Live) setStatus("Karma V11 live");
  }, [v11Live]);

  const brandAccent = activeProfile?.brand?.primaryColor ?? accent;

  useEffect(() => {
    const unsubs = [
      eventBus.on("SPATIAL:TILES_COUNT", ({ count }: { count: number }) => {
        setTileCount(count);
      }),
      eventBus.on("SPATIAL:TILES_LIST", ({ tiles }: { tiles: ActiveTileInfo[] }) => {
        setActiveTiles(tiles);
      }),
      eventBus.on("MINDALOO:PANEL", (payload: { panel?: string }) => {
        if (payload.panel === "memory") setShowMemory(true);
      }),
      eventBus.on(
        "MINDALOO:OPEN_PANEL",
        ({ panel }: { panel: string }) => {
          if (panel === "appearance") { setShowAppearance(true); return; }
          // "builder"/"chat"/"multichat" panels were retired — Studio and
          // Thought Stream folded into Settings, Council is just opening
          // tiles on the canvas (see BUILDER:OPEN/MULTICHAT:OPEN below).
          if (panel === "builder" || panel === "chat") { setSlidePanel("settings"); return; }
          if (panel === "multichat") {
            if (latestActiveProfileRef.current) latestOpenProfileRef.current(latestActiveProfileRef.current);
            return;
          }
          if (panel) setSlidePanel(panel as SlidePanel);
        }
      ),
      // Every channel this used to fan out to a canned "connect to X, what
      // should we do?" chat prompt (vaults, marketplace, gmail, calendar,
      // weather, calculator, ...) has been removed from the Neural Shelf —
      // see lib/karma-ac/actions.ts. What's left are the destinations that
      // actually do something real.
      eventBus.on("MINDALOO:BRIDGE", async (payload: { channel: string }) => {
        const channel = payload.channel;

        if (channel === "multichat") {
          if (latestActiveProfileRef.current) latestOpenProfileRef.current(latestActiveProfileRef.current);
          return;
        }
        if (channel === "builder" || channel === "chat") { setSlidePanel("finetune"); return; }
        if (channel === "settings") { setSlidePanel("settings"); return; }
        if (channel === "thought-stream") { setShowThoughtStream(true); return; }
      }),
      // Direct orb events (KarmaOrb also emits these alongside onAction).
      // Council/Studio used to open a modal that reimplemented tile
      // grid/stack/link — that's just opening tiles on the real canvas now,
      // so Council drops a second instance of the active profile straight
      // onto the canvas (same as tapping it in the shelf); Studio's
      // identity/personality controls live in Settings.
      eventBus.on("MULTICHAT:OPEN", () => {
        if (latestActiveProfileRef.current) latestOpenProfileRef.current(latestActiveProfileRef.current);
      }),
      // Quick-add button in the top bar (see KarmaDynamicIsland.tsx) — same
      // "drop the active profile on canvas" action as Council above, just
      // reachable without a trip through the shelf.
      eventBus.on("SPATIAL:QUICK_ADD_TILE", () => {
        if (latestActiveProfileRef.current) latestOpenProfileRef.current(latestActiveProfileRef.current);
      }),
      // "Studio" — full identity/personality/appearance/behavior editor,
      // see the "finetune" slide panel below.
      eventBus.on("BUILDER:OPEN", () => setSlidePanel("finetune")),
      eventBus.on("SHELF:OPEN", () => setShowShelf(true)),
      eventBus.on("MEMORY:OPEN", () => setShowMemory(true)),
      eventBus.on("MIRROR:OPEN", () => setShowMirror(true)),
      eventBus.on("THEME:OPEN", () => setTheme((t) => (t === "night" ? "day" : "night"))),
      eventBus.on("THEME:TOGGLE", () => setTheme((t) => (t === "night" ? "day" : "night"))),
      eventBus.on("SETTINGS:OPEN", () => setSlidePanel("settings")),
      // The appearance panel (MindalooAppearancePanel below) already existed
      // and already worked once opened — it just had no way to be opened.
      // dispatchKarmaAction's "customize" action was firing an event with no
      // listener at all.
      eventBus.on("APPEARANCE:OPEN", () => setShowAppearance(true)),
      eventBus.on("AUTONOMOUS:OPEN", () => setShowAutonomous(true)),
      // Voice pilot actions
      eventBus.on("SPATIAL:OPEN_CHAT_WITH_PROFILE", ({ profileId }: { profileId: string }) => {
        const lower = profileId.toLowerCase();
        const p = latestProfilesRef.current.find((x) => x.id === profileId || x.name.toLowerCase() === lower);
        // A voice-pilot request for a name that doesn't match any real
        // profile (misheard audio, a name that doesn't exist) used to
        // silently fall back to reopening whichever profile happened to be
        // active — Karma would say "Opening it now" and a tile the user
        // never asked for would appear, with no sign anything went wrong.
        // Doing nothing here is the safer default than cluttering the
        // canvas with an unwanted duplicate.
        if (p) {
          latestOpenProfileRef.current(p);
        } else {
          console.warn(`[voice pilot] no profile matched "${profileId}" — not opening a fallback tile`);
        }
      }),
      eventBus.on("SPATIAL:SEND_TO_TILE", ({ tileId, message }: { tileId: string; message: string }) => {
        window.dispatchEvent(new CustomEvent("multiChatSendFromOverview", { detail: { chatId: tileId, message } }));
      }),
      eventBus.on("SPATIAL:CREATE_STICKY", ({ content }: { content: string }) => {
        eventBus.emit("SPATIAL:QUICK_COMMAND", { text: `Create a sticky note: ${content}` });
      }),
      eventBus.on("AWARENESS:SET", ({ state }: { state: string }) => {
        if (state === "active") setAwarenessState(AWARENESS_STATES.ACTIVE);
        else if (state === "dormant") setAwarenessState(AWARENESS_STATES.DORMANT);
        else setAwarenessState(AWARENESS_STATES.FOCUSED);
      }),
      eventBus.on("MINDALOO:DISMISS_ALL", () => {
        setShowShelf(false);
        setShowThoughtStream(false);
        setShowMemory(false);
        setShowMirror(false);
        setShowAutonomous(false);
        setShowAppearance(false);
        setSlidePanel(null);
      }),
      eventBus.on("KARMA:SAVE_MEMORY", async ({ category, key, value }: { category: string; key: string; value: string }) => {
        const profile = latestActiveProfileRef.current;
        if (!profile) return;
        try {
          await fetch("/api/assistant-memory", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId,
              aiId: profile.id,
              key: `${category}.${key}`,
              value: String(value),
              scope: "user",
              channelId: "voice-pilot",
            }),
          });
        } catch (err) {
          console.error("[voice-pilot] saveMemory failed:", err);
        }
      }),
      eventBus.on("KARMA:CREATE_REMINDER", ({ date, time, message }: { date: string; time: string; message: string }) => {
        const due = new Date(`${date}T${time}`).getTime();
        const now = Date.now();
        const delay = Math.max(0, due - now);
        if (delay > 0) {
          setTimeout(() => {
            if ("Notification" in window && Notification.permission === "granted") {
              new Notification("Karma reminder", { body: message });
            } else {
              setAtlasProcess({ status: "ready", message: `Reminder: ${message}` });
            }
          }, delay);
        }
      }),
      eventBus.on("KARMA:SYSTEM_STATUS", ({ status }: { status: string }) => {
        setAtlasProcess({ status: "ready", message: `System status: ${status}` });
      }),
      eventBus.on("KARMA:VOICE_STOP", () => {
        voicePilotRef.current?.stop();
        setAtlasProcess({ status: "ready", message: "Voice session stopped." });
        setVoiceThinking(false);
      }),
    ];
    return () => unsubs.forEach((u) => u());
  }, [setAtlasProcess]);

  const handleSend = useCallback(async (textFromBar: string) => {
    const text = textFromBar.trim();
    if (!text || !activeProfile) return;

    setAwarenessState(AWARENESS_STATES.ACTIVE);
    setStatus("Atlas navigating…");
    setCmdLoading(true);
    setVoiceContinuous(false);

    const messageId = crypto.randomUUID();
    // Deliberately not awaited: this drives the top-bar status text and the
    // "Expand Atlas" panel's history, neither of which the tile needs to
    // hear back from before it can answer. Awaiting it here used to make
    // every send pay for a full Atlas round-trip (~10-15s) BEFORE the
    // message even reached the tile — which then ran its own separate
    // Atlas pass internally before generating a reply. Two serial Atlas
    // round-trips per message was the real reason simple messages could
    // take 30+ seconds to produce anything, especially with several tiles
    // in flight. Letting this run in the background instead means the
    // global panel still fills in once it resolves, but no longer blocks
    // the actual conversation.
    navigateForMessage(text, messageId).catch(() => {
      /* atlas optional */
    });

    // Every tile's own composer sits collapsed by default (see
    // composerExpanded in ChatInterface.tsx) — this bar is the normal way
    // to type to any of them, in every layout (fullscreen-maximized, grid,
    // carousel, scatter), not just the maximized one. Checking `.maximized`
    // here only covered the fullscreen case: a tile you're actively looking
    // at in grid/carousel never sets that flag, so it silently lost every
    // message to an unrelated linked group elsewhere on the canvas — found
    // live testing an unlinked tile alongside a linked pair. The focused
    // tile now wins whenever it isn't itself part of the group being
    // linked, since "linked" specifically means "hears the bar together" —
    // a tile that opted into that should still get the group behavior.
    const activeTile = activeTileRef.current;
    const activeIsLinked = !!activeTile && linkedTileIdsRef.current.includes(activeTile.id);
    if (activeTile && !activeIsLinked) {
      eventBus.emit("SPATIAL:SEND_TO_TILE", { tileId: activeTile.id, message: text });
    } else if (linkedTileIdsRef.current.length >= 2) {
      // Named one specific tile — talk to just that one, not the group.
      // Otherwise every linked tile answers independently, in its own
      // voice, through its own full pipeline — quick and casual, each tile
      // just gets asked. The Council panel (see handleAskCouncil below) is
      // the deliberate, different mode: those AIs actually confer with each
      // other before answering, this doesn't.
      const targets = resolveBroadcastTargets(text, linkedTileIdsRef.current, activeTilesRef.current);
      targets.forEach((id) => eventBus.emit("SPATIAL:SEND_TO_TILE", { tileId: id, message: text }));
    } else if (activeTile) {
      eventBus.emit("SPATIAL:SEND_TO_TILE", { tileId: activeTile.id, message: text });
    } else {
      eventBus.emit("SPATIAL:QUICK_COMMAND", { text });
    }
    setInput("");
    setStatus(tileCount > 0 ? "Spatial canvas" : "Focused");
    setAwarenessState(AWARENESS_STATES.FOCUSED);
    setCmdLoading(false);
  }, [activeProfile, navigateForMessage, setAwarenessState, tileCount]);

  // Council/Roundtable panel's explicit multi-select "ask" — a real
  // deliberation, not independent replies. Two phases: round 1 (every tile
  // answers independently) lands in councilRound1 so the panel can show the
  // spread and let the user pick one to steer toward, instead of the
  // deliberation always averaging everyone equally; handleCouncilSteer
  // resumes from there. A single selected tile skips deliberation
  // entirely — nothing to confer about — and is just a normal direct send.
  const [councilRound1, setCouncilRound1] = useState<{
    question: string;
    tiles: CouncilTile[];
    results: CouncilRoundResult[];
  } | null>(null);
  // Persists only while the Council panel stays open on this topic (cleared
  // on close, see the CouncilPanel render below) — a follow-up question
  // starts already grounded in whatever the room settled on last time
  // instead of re-litigating from zero.
  const [councilSession, setCouncilSession] = useState<string | null>(null);

  const handleAskCouncil = useCallback(async (question: string, tileIds: string[]) => {
    if (tileIds.length === 1) {
      eventBus.emit("SPATIAL:SEND_TO_TILE", { tileId: tileIds[0], message: question });
      return;
    }
    const councilTiles: CouncilTile[] = tileIds
      .map((id) => activeTilesRef.current.find((t) => t.id === id))
      .filter((t): t is ActiveTileInfo => !!t && !!t.profile)
      .map((t) => ({ id: t.id, name: t.name, profile: t.profile! }));
    if (councilTiles.length < 2) return;
    try {
      const results = await getCouncilRound1(question, councilTiles, getUserId(), councilSession);
      setCouncilRound1({ question, tiles: councilTiles, results });
      // Each tile gets its own honest round-1 answer in its own history —
      // this used to only render inside the Council panel's picker, so the
      // tiles themselves stayed empty until a single collapsed verdict got
      // broadcast into all of them at the end (see handleCouncilSteer).
      results.forEach((r) => {
        eventBus.emit("SPATIAL:INJECT_USER_MESSAGE", { tileId: r.tile.id, content: question });
        eventBus.emit("SPATIAL:INJECT_ASSISTANT_MESSAGE", { tileId: r.tile.id, content: r.text });
      });
    } catch (err) {
      console.error("[council round1] failed:", err);
      clearConferring(councilTiles);
      tileIds.forEach((id) =>
        eventBus.emit("SPATIAL:INJECT_ASSISTANT_MESSAGE", {
          tileId: id,
          content: "The council couldn't get started this time — try again in a moment.",
        })
      );
    }
  }, [councilSession]);

  // steerTileId null = "let them decide together" (the original, unsteered
  // behavior — everyone weighs all round-1 answers equally).
  const handleCouncilSteer = useCallback((steerTileId: string | null) => {
    if (!councilRound1) return;
    const { question, tiles, results } = councilRound1;
    const tileIds = tiles.map((t) => t.id);
    setCouncilRound1(null);
    continueCouncilDeliberation({ text: question, tiles, round1: results, steerTileId, userId: getUserId() })
      .then(({ round2, synthesis }) => {
        setCouncilSession(synthesis);
        // Each participant's own round-2 refinement goes into its own
        // tile — this is the actual fix for "they all say the same
        // thing": round 2 already computed a distinct, individually-voiced
        // reply per tile, but the old code discarded it and injected the
        // single collapsed synthesis into every tile identically instead.
        // The synthesis itself is kept as session memory (for a follow-up
        // question) rather than force-fed into every tile's own history.
        round2.forEach((r) => {
          eventBus.emit("SPATIAL:INJECT_ASSISTANT_MESSAGE", { tileId: r.tile.id, content: r.text });
        });
      })
      .catch((err) => {
        console.error("[council continue] failed:", err);
        tileIds.forEach((id) =>
          eventBus.emit("SPATIAL:INJECT_ASSISTANT_MESSAGE", {
            tileId: id,
            content: "The council couldn't reach a final answer this time — try again in a moment.",
          })
        );
      });
  }, [councilRound1]);

  const handleVoicePilot = useCallback(async (text: string) => {
    const profile = activeProfile ?? DEFAULT_KARMA_PROFILE;
    if (!text || !voicePilotRef.current) {
      console.warn("[VoicePilot] early return — missing text or session");
      return;
    }

    // Ignore duplicate transcripts within 3 seconds (echo / recognition double-fire)
    const now = Date.now();
    if (text === lastVoiceTextRef.current && now - lastVoiceTsRef.current < 3000) {
      console.warn("[VoicePilot] duplicate transcript ignored:", text);
      return;
    }
    lastVoiceTextRef.current = text;
    lastVoiceTsRef.current = now;

    // Reject a second transcript that arrives while a turn is still being
    // processed or spoken. Confirmed live: two calls landed back-to-back
    // with no TTS-ended in between, and both played audio simultaneously
    // ("duplicate voices"). Stopping the mic (below) prevents *future*
    // recognitions from starting a new turn, but doesn't retroactively
    // cancel one that was already in flight the instant this fired.
    if (voiceTurnInFlightRef.current) {
      console.warn("[VoicePilot] turn already in flight, ignoring:", text);
      return;
    }
    voiceTurnInFlightRef.current = true;
    // Captured now, checked again after the await below — if a stop, a
    // manual mic restart, or a new transcript started a fresher turn while
    // this one's fetch/TTS was still resolving, myGeneration no longer
    // matches and this turn's result is discarded instead of acted on.
    const myGeneration = ++voiceTurnGenerationRef.current;
    // Safety net: every real exit path below clears this explicitly, but if
    // some unforeseen path doesn't, a stuck `true` here would silently
    // ignore all future voice input forever. Force-clear as a last resort.
    setTimeout(() => {
      voiceTurnInFlightRef.current = false;
    }, 25000);

    // Hard-stop the mic the instant we start processing this turn, rather
    // than relying on the echo detector's post-hoc guess at whether a
    // transcript arrived "during" an estimated TTS window. The native
    // recognizer's own auto-restart is a ~150ms timer completely
    // independent of how long the LLM call + TTS takes, so without this it
    // can easily be listening again well before Karma finishes speaking.
    // .stop() also cancels that auto-restart, so nothing re-arms the mic
    // until we explicitly call .start() again once we know it's safe to.
    voiceInputRef.current?.stop();

    setVoiceThinking(true);
    setStatus("Karma is thinking…");
    setAtlasProcess({ status: "planning", message: `You: ${text}` });

    try {
      const workspaceContext = {
        activeTiles: activeTiles.map((t) => ({
          id: t.id,
          name: t.name,
          type: t.type,
          profileName: t.profile?.name,
          minimized: t.minimized,
        })),
        availableProfiles: (profiles ?? []).map((p) => ({ id: p.id, name: p.name })),
        awareness,
        status,
        recentCommands: [
          ...(linkedTileIdsRef.current.length >= 2
            ? [
                `Linked tiles (they receive nav-bar messages together and share perspectives): ${activeTiles
                  .filter((t) => linkedTileIdsRef.current.includes(t.id))
                  .map((t) => t.name)
                  .join(", ")}`,
              ]
            : []),
          ...recentExchangesRef.current.slice(-5).map(
            (ex) =>
              `[${ex.tileName}] user: "${ex.userText.slice(0, 120)}" → ai: "${ex.assistantText.slice(0, 200)}"`
          ),
        ],
      };

      const result = await voicePilotRef.current.processTranscript(text, profile, workspaceContext);

      // A newer turn started (stop, manual restart, or another transcript)
      // while this one's request was still in flight — its result is stale.
      // Clearing the in-flight flag would incorrectly release the *new*
      // turn's lock, so this only bails out of acting on the result; it
      // deliberately does not touch voiceTurnInFlightRef.
      if (voiceTurnGenerationRef.current !== myGeneration) {
        console.warn("[VoicePilot] discarding stale turn result:", text);
        return;
      }

      if (result.stop) {
        voiceTurnInFlightRef.current = false;
        setVoiceContinuous(false);
        setAtlasProcess({ status: "ready", message: "Stopped listening." });
        setStatus("Focused");
        return;
      }

      if (!result.directed) {
        // Speech not directed at Karma — keep listening but don't respond.
        // Nothing is being spoken here, so unlike the normal response path
        // there's no TTS onended callback to restart the mic — do it now.
        voiceTurnInFlightRef.current = false;
        setAtlasProcess({ status: "ready", message: "Listening…" });
        setStatus("Focused");
        if (voiceContinuousRef.current) voiceInputRef.current?.start();
        return;
      }

      setVoiceSession((prev) => [
        ...prev,
        { role: "user", content: text },
        { role: "assistant", content: result.message },
      ]);
      setAtlasProcess({ status: "ready", message: `Karma: ${result.message}` });
      setStatus(result.needsConfirmation ? "Awaiting confirmation" : "Focused");

      // Stay hands-free for back-and-forth unless we need explicit confirmation
      setVoiceContinuous(!result.needsConfirmation);

      // Multi-step request (a later step needs an earlier step's real
      // output) — hand off to the background planner instead of the normal
      // single-shot action batch. Deliberately not awaited: this turn's mic
      // reentrancy lock / TTS handling below must not block on a plan that
      // could run for several sequential LLM calls. Scoped to whichever tile
      // is currently active, if any, so it runs as that tile's own task
      // rather than an untargeted background one — and a second tile can
      // start its own independent plan the same way without the two
      // colliding (see runningTiles in taskPlanner.ts).
      if (result.needsPlanning) {
        const active = activeTileRef.current;
        runPlannedTask(text, profile, workspaceContext, active ? { tileId: active.id, tileName: active.name } : undefined).catch((err) => {
          console.error("[VoicePilot] planned task failed:", err);
        });
      }

      // Execute any actions Karma decided to take
      if (result.actions && result.actions.length > 0) {
        const actionResults = executeVoicePilotActions(result.actions);
        // This return value used to be discarded entirely — a failed
        // action (unknown type, missing param, popup blocked, etc.) was
        // completely invisible, which is exactly what let the {name,
        // parameters} vs {type,...} shape mismatch go unnoticed: Karma
        // said "Opening it now" while every single action silently failed.
        const failures = actionResults.filter((r) => !r.success);
        if (failures.length > 0) {
          console.warn("[VoicePilot] action(s) failed:", failures);
          eventBus.emit("TOAST:SHOW", {
            message: `⚠️ ${failures.map((f) => f.message).join("; ")}`,
            type: "error",
            duration: 5000,
          });
        }
      }

      // Surface what Karma is doing in the Atlas strip and as a toast
      if (result.message) {
        eventBus.emit("TOAST:SHOW", {
          message: `Karma: ${result.message.length > 100 ? result.message.slice(0, 100) + "…" : result.message}`,
          type: "info",
          duration: 4000,
        });
      }

      // Play spoken response and track TTS so echo detector can ignore playback
      const finishTtsAndMaybeRestart = () => {
        voiceTurnInFlightRef.current = false;
        voicePilotRef.current?.trackTtsFinish();
        if (voiceContinuousRef.current) {
          setTimeout(() => {
            if (voiceContinuousRef.current) {
              voiceInputRef.current?.start();
            }
          }, 1500);
        }
      };

      const speakWithFallback = async (message: string, audioUrl?: string) => {
        const ttsDuration = estimateTtsDuration(message);
        voicePilotRef.current?.setTtsPlaying(ttsDuration);

        const fallbackToSpeechSynthesis = () => {
          if (typeof window !== "undefined" && "speechSynthesis" in window) {
            const utterance = new SpeechSynthesisUtterance(message);
            utterance.onend = finishTtsAndMaybeRestart;
            utterance.onerror = (e) => {
              console.warn("[VoicePilot] speechSynthesis error:", e);
              finishTtsAndMaybeRestart();
            };
            window.speechSynthesis.speak(utterance);
          } else {
            console.warn("[VoicePilot] no audio.play() and no speechSynthesis available — nothing will be heard");
            finishTtsAndMaybeRestart();
          }
        };

        if (audioUrl) {
          const audio = new Audio(audioUrl);
          audio.onended = finishTtsAndMaybeRestart;
          audio.onerror = (e) => {
            console.warn("[VoicePilot] <audio> playback error, falling back:", e);
            fallbackToSpeechSynthesis();
          };
          try {
            await audio.play();
          } catch (err) {
            console.warn("[VoicePilot] audio.play() rejected, falling back:", err);
            fallbackToSpeechSynthesis();
          }
        } else {
          fallbackToSpeechSynthesis();
        }
      };

      if (result.message) {
        // Guard released inside finishTtsAndMaybeRestart once audio (or its
        // speechSynthesis fallback) genuinely finishes — not here, since
        // this call isn't awaited and resolves as soon as playback starts.
        speakWithFallback(result.message, result.audio?.url);
      } else {
        // No message and no TTS to wait for — this turn ends now.
        voiceTurnInFlightRef.current = false;
        if (voiceContinuousRef.current) voiceInputRef.current?.start();
      }
    } catch (err: any) {
      voiceTurnInFlightRef.current = false;
      console.error("Voice pilot failed:", err);
      setVoiceContinuous(false);
      const errorMsg = err?.message || String(err) || "Karma couldn't respond right now.";
      setAtlasProcess({ status: "ready", message: `Voice error: ${errorMsg}` });
      setStatus("Focused");
      eventBus.emit("TOAST:SHOW", {
        message: `Voice error: ${errorMsg}`,
        type: "error",
        duration: 6000,
      });
    } finally {
      setVoiceThinking(false);
    }
  }, [activeProfile, activeTiles, profiles, awareness, status, setAtlasProcess, voiceContinuous]);

  useEffect(() => {
    if (voiceThinking) return; // thinking state takes precedence
    if (voiceListening) {
      setAtlasProcess({ status: "ready", message: "Listening…" });
    }
  }, [voiceListening, voiceThinking, setAtlasProcess]);

  const handleProfileUpdate = async (updated: AiProfile) => {
    setActiveProfile(updated);
    await db.aiProfiles.put(updated);
  };

  const openProfileOnCanvas = (p: AiProfile) => {
    const tileId = `profile-${Date.now()}-${p.id}`;
    // Numbering ("Mindaloo AC 2", ...) against other open instances of the
    // same profile actually happens in SpatialCommandCenter's DROP_TILE
    // handler, not here — activeTilesRef can be a render behind whenever
    // two tiles get created close together (a click and a voice-pilot
    // action, say), so this name is just a placeholder the handler
    // overwrites once it can see the real, current tile list.
    eventBus.emit("SPATIAL:DROP_TILE", {
      tile: {
        id: tileId,
        type: "chat",
        name: p.name,
        icon: "💬",
        profile: p,
        metadata: { prompt: "" },
        chromeless: true,
        content: <MindalooChatTile profile={p} tileId={tileId} theme={theme} />,
      },
    });
    setShowShelf(false);
  };

  // History Box resume: reopens every tile in an archived group under its
  // original id, which is also its conversationId — ChatInterface's own
  // loadMessages() picks the existing history back up automatically, so
  // this only has to recreate the tiles and re-link them, not move or copy
  // any messages. A profile deleted since the session was archived just
  // gets skipped rather than blocking the whole resume.
  const resumeArchivedSession = useCallback((session: MultiChatSession) => {
    const resumedIds: string[] = [];
    let skipped = 0;
    for (const snap of session.tileSnapshots) {
      const p = profiles.find((pr) => pr.id === snap.profileId);
      if (!p) { skipped++; continue; }
      eventBus.emit("SPATIAL:DROP_TILE", {
        tile: {
          id: snap.tileId,
          type: "chat",
          name: snap.tileName,
          icon: "💬",
          profile: p,
          metadata: { prompt: "" },
          chromeless: true,
          content: <MindalooChatTile profile={p} tileId={snap.tileId} theme={theme} />,
        },
      });
      resumedIds.push(snap.tileId);
    }
    if (resumedIds.length >= 2) {
      setLinkedTileIds(resumedIds);
      setPrimaryTileIdState(session.primaryTileId && resumedIds.includes(session.primaryTileId) ? session.primaryTileId : null);
    }
    if (skipped > 0) {
      eventBus.emit("TOAST:SHOW", {
        message: `Resumed — ${skipped} tile${skipped === 1 ? "" : "s"} couldn't reopen (its AC no longer exists).`,
        type: "info",
      });
    }
    setShowShelf(false);
  }, [profiles, theme]);

  useEffect(() => {
    latestOpenProfileRef.current = openProfileOnCanvas;
  }, [openProfileOnCanvas]);

  if (loading || !activeProfile) {
    return (
      <div className="karma-ac-app mindaloo-app flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#C1A2FF]/30 border-t-[#C1A2FF] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <MindalooErrorBoundary context="MindalooShell">
      {shouldOnboard && (
        <MindalooOnboarding
          onComplete={completeOnboarding}
          profileName={activeProfile?.name}
        />
      )}
      <div
        className={`karma-ac-app mindaloo-app ${theme === "day" ? "karma-day" : ""} awareness-${awareness}`}
        style={{ "--karma-accent": brandAccent } as React.CSSProperties}
      >
        <BrandStyles brand={activeProfile.brand} />
        {theme !== "day" && <MindalooStarfield />}

      <div className="karma-ui-top">
        <KarmaDynamicIsland
          accent={brandAccent}
          statusText={status}
          theme={theme}
          atlasProcess={atlasProcess}
          v11Live={v11Live}
          v11Preview={v11Preview}
          activeTiles={activeTiles}
          linkedTileIds={linkedTileIds}
          onToggleTileLink={toggleTileLink}
          primaryTileId={primaryTileId}
          onSetPrimaryTile={setPrimaryTile}
        />
              </div>

      {!showShelf && (
        <div
          className="karma-shelf-tab"
          role="button"
          tabIndex={0}
          onClick={() => setShowShelf(true)}
          onKeyDown={(e) => e.key === "Enter" && setShowShelf(true)}
          aria-label="Open Neural Shelf"
        />
      )}

      <AnimatePresence>
        {showShelf && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[1490] bg-black/40 backdrop-blur-md"
              onClick={() => setShowShelf(false)}
            />
            <motion.aside
              initial={{ x: -360 }}
              animate={{ x: 0 }}
              exit={{ x: -360 }}
              transition={{ type: "spring", stiffness: 400, damping: 35 }}
              className="fixed left-0 top-0 bottom-0 w-[min(360px,90vw)] z-[1500] karma-glass karma-neon-ring"
            >
              <KarmaNeuralShelf
                profiles={profiles}
                onClose={() => setShowShelf(false)}
                onOpenProfile={openProfileOnCanvas}
                onResumeSession={resumeArchivedSession}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="karma-canvas-layer mindaloo-canvas-layer">
        <SpatialCommandCenter
          accent={brandAccent}
          theme={theme}
          className="karma-spatial h-full"
          profile={activeProfile}
        />
      </div>

      {tileCount === 0 && (
        <div className="karma-heartbeat pointer-events-none">
          {/* Nebula bloom — radial glow behind logo */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              width: 520,
              height: 520,
              borderRadius: "50%",
              background: isPremiumGodMode
                ? "radial-gradient(ellipse at center, rgba(147,51,234,0.18) 0%, rgba(100,30,200,0.10) 35%, transparent 70%)"
                : "radial-gradient(ellipse at center, rgba(193,162,255,0.12) 0%, rgba(80,40,160,0.07) 38%, transparent 70%)",
              pointerEvents: "none",
              zIndex: 1,
              animation: "nebula-bloom 5s ease-in-out infinite",
            }}
          />
          {/* Horizon glow streak below logo */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: "68%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 600,
              height: 1,
              background: isPremiumGodMode
                ? "linear-gradient(90deg, transparent, rgba(147,51,234,0.35) 30%, rgba(193,162,255,0.55) 50%, rgba(147,51,234,0.35) 70%, transparent)"
                : "linear-gradient(90deg, transparent, rgba(193,162,255,0.20) 30%, rgba(193,162,255,0.38) 50%, rgba(193,162,255,0.20) 70%, transparent)",
              boxShadow: "0 0 18px 4px rgba(193,162,255,0.15)",
              borderRadius: 1,
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          <div
            className={`karma-heartbeat-pulse ${isPremiumGodMode ? "drop-shadow-[0_0_30px_rgba(147,51,234,0.4)]" : "drop-shadow-[0_0_24px_rgba(193,162,255,0.22)]"}`}
            style={{ position: "relative", zIndex: 2 }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/mindaloo-symbol.png"
              alt="Mindaloo"
              style={{
                width: 340,
                height: "auto",
                mixBlendMode: "screen",
                opacity: isPremiumGodMode ? 0.88 : 0.75,
                filter: isPremiumGodMode
                  ? "hue-rotate(30deg) brightness(1.15)"
                  : "brightness(1.08) contrast(1.05)",
              }}
            />
          </div>
          {systemTelemetry && (
            <p className="mt-3 text-[9px] font-mono text-purple-400/80 uppercase tracking-wider px-4 text-center">
              {systemTelemetry}
            </p>
          )}
          
                  </div>
      )}

      <KarmaCommandBar
        profile={activeProfile}
        accent={brandAccent}
        input={input}
        onInputChange={setInput}
        onSend={handleSend}
        loading={cmdLoading}
        onVoiceListeningChange={setVoiceListening}
        onVoicePilot={handleVoicePilot}
        onVoiceStart={() => voicePilotRef.current?.warmUp()}
        onVoiceStop={() => {
          // Manually stopping the mic mid-turn should mean the in-flight
          // turn's eventual result (if any) never gets acted on — see
          // voiceTurnGenerationRef above.
          voiceTurnGenerationRef.current++;
          setVoiceContinuous(false);
        }}
        voiceContinuous={voiceContinuous}
        voiceInputRef={voiceInputRef}
        responseMode={responseMode}
        onResponseModeChange={setResponseMode}
        atlasProcess={atlasProcess}
        atlasNavigationResult={atlasNavigationResult}
        atlasHistory={atlasHistory}
        activeTiles={activeTiles}
        v11Lines={v11Lines}
        v11Live={v11Live}
        voiceSession={voiceSession}
        onCouncilToggle={() => setShowCouncil((s) => !s)}
        dimmed={showCouncil}
        raisedHands={raisedHands}
        onClearRaisedHand={(id) => setRaisedHands((prev) => prev.filter((h) => h.triggerId !== id))}
        onAtlasOutcome={(outcome) => {
          const latestNavigation = atlasHistory[atlasHistory.length - 1];
          if (latestNavigation) recordOutcome(latestNavigation.messageId, outcome);
        }}
      />

      <VoiceGlowRing open={voiceListening} profile={activeProfile} />

      <MindalooThoughtStream
        open={showThoughtStream}
        onClose={() => setShowThoughtStream(false)}
        lines={v11Lines}
        connected={v11Live}
        accent={brandAccent}
        userId={userId}
      />
      <MindalooMemoryPanel open={showMemory} onClose={() => setShowMemory(false)} userId={userId} />
      <MindalooMirrorPanel open={showMirror} onClose={() => setShowMirror(false)} userId={userId} accent={brandAccent} />
      <MindalooAutonomousPanel
        open={showAutonomous}
        onClose={() => setShowAutonomous(false)}
        onEnable={() => {
          setShowAutonomous(false);
          // Used to open the (now-retired) Multi-Agent modal — enabling
          // autonomous mode's real next step is a second tile on the canvas.
          if (latestActiveProfileRef.current) latestOpenProfileRef.current(latestActiveProfileRef.current);
        }}
      />

      <MindalooSlideOver
        panel={slidePanel}
        profile={activeProfile}
        profiles={profiles}
        onProfileUpdate={handleProfileUpdate}
        onClose={() => setSlidePanel(null)}
      />

      <MindalooAppearancePanel
        open={showAppearance}
        profile={activeProfile}
        onClose={() => setShowAppearance(false)}
        onProfileUpdate={(updated: AiProfile) => {
          handleProfileUpdate(updated);
          // mindalooTheme is not yet in BrandConfig — theme changes handled via THEME:TOGGLE
        }}
      />

      <AnimatePresence>
        {showCouncil && (
          <CouncilPanel
            tiles={activeTiles.map((t) => ({ id: t.id, name: t.name, type: t.type, profileName: t.profile?.name }))}
            onAsk={handleAskCouncil}
            pendingRound1={councilRound1}
            onSteer={handleCouncilSteer}
            onClose={() => {
              setShowCouncil(false);
              setCouncilSession(null);
            }}
            accent={brandAccent}
          />
        )}
      </AnimatePresence>

      <CommandPalette />
      {/* Defined and fully built (eventBus "TOAST:SHOW" listener + UI) but
          never actually mounted anywhere — every TOAST:SHOW emit in the app
          (voice errors, offline-queue notices, etc.) fired into the void. */}
      <ToastContainer />
    </div>
    </MindalooErrorBoundary>
  );
}
